import type { IntlShape } from 'react-intl-next';

import { ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import { areToolbarFlagsEnabled } from '@atlaskit/editor-common/toolbar-flag-check';
import type {
	Command,
	ExtractInjectionAPI,
	FloatingToolbarHandler,
	FloatingToolbarItem,
	FloatingToolbarSeparator,
} from '@atlaskit/editor-common/types';
import type { Node, NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findParentNode } from '@atlaskit/editor-prosemirror/utils';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type { AIPlugin } from '../../aiPluginType';
import { aiExperienceDecorationPluginKey } from '../../pm-plugins/decoration/decoration-plugin-key';
import { InlineSuggestionToolbar } from '../../pm-plugins/inline-suggestions/floating-toolbar/floating-toolbar';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { getLinkMarkAtCursor, getSupportedNodesForAIModal } from '../utils/ai-button';
import { fireButtonViewedAnalyticsEvent, selectionCharWordCount } from '../utils/analytics/utils';

import {
	getAIExperienceButton,
	getAskAIButton,
	getAskRovoAiSplitButton,
	getSmartLinkAIButtons,
} from './utils/floating-toolbar-button';
import { getIsAiEntryPointDisabled } from './utils/use-is-ai-entry-point-disabled';

const getAIButton = ({
	intl,
	editorPluginAIProvider,
	aiGlobalOptIn,
	nodeType,
	disabled,
	api,
	state,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	disabled: boolean;
	editorPluginAIProvider: EditorPluginAIProvider;
	intl: IntlShape;
	nodeType: NodeType;
	state: EditorState;
}) => {
	const isAIEnabled = aiGlobalOptIn.status === 'enabled';

	if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
		const handleAiButtonMount = () => {
			const { charCount, wordCount } = selectionCharWordCount(state);
			fireButtonViewedAnalyticsEvent({
				api,
				actionSubjectId: ACTION_SUBJECT_ID.AI_ASK_ROVO_BUTTON,
				attributes: {
					selectionCharCount: charCount,
					selectionWordCount: wordCount,
				},
			});
		};

		return getAskRovoAiSplitButton({
			intl,
			rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
			aiGlobalOptIn,
			triggeredFor: nodeType,
			disabled,
			editorPluginAIProvider,
			api,
			onMount: handleAiButtonMount,
		});
	}
	if (
		(isAIEnabled && fg('ai-smart-button-team-25')) ||
		editorExperiment('platform_editor_controls', 'variant1')
	) {
		return getAskAIButton({
			intl,
			rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
			aiGlobalOptIn,
			triggeredFor: nodeType,
			disabled,
		});
	}

	return getAIExperienceButton({
		intl,
		rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
		hideTitle: true,
		aiGlobalOptIn,
		triggeredFor: nodeType,
		disabled,
	});
};
export const getFloatingToolbarConfig =
	({
		aiGlobalOptIn,
		editorPluginAIProvider,
		api,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		api: ExtractInjectionAPI<AIPlugin> | undefined;
		editorPluginAIProvider: EditorPluginAIProvider;
	}): FloatingToolbarHandler =>
	// eslint-disable-next-line @typescript-eslint/max-params
	(state, intl, _providerFactory, processedConfigs) => {
		const aiDecorationPluginState = aiExperienceDecorationPluginKey.getState(state);
		const aiModalDecorationSet = aiDecorationPluginState?.modalDecorationSet ?? DecorationSet.empty;
		const isAIModalOpen = aiModalDecorationSet !== DecorationSet.empty;

		if (isAIModalOpen && editorPluginAIProvider.isInDocumentStreamingEnabled) {
			return {
				title: '',
				visible: false,
				items: [],
				nodeType: Object.values(state.schema.nodes).reduce((acc, cur) => {
					if (cur.isBlock) {
						acc.push(cur);
					}
					return acc;
				}, [] as NodeType[]),
			};
		}

		// Following line is to ensure that the inline suggestion toolbar is always shown when the AI modal is open
		const suggestionToolbar = InlineSuggestionToolbar.maybeBuild(state, api);
		if (suggestionToolbar) {
			return suggestionToolbar;
		}

		/**
		 * Add AI button in nodes supported (currently only table is supported).
		 * List of supported node can be found in implementation of getSupportedNodesForAIModal.
		 */
		const { selection } = state;
		const { from, to } = selection;
		const isNodeSelection = selection instanceof NodeSelection;
		const isCellSelection = selection.toJSON().type === 'cell';
		const getParentSupportedForAIButton = () => {
			const supportedNodesForAI = getSupportedNodesForAIModal(state);

			if (isNodeSelection) {
				return supportedNodesForAI.has(selection.node.type) ? selection.node : undefined;
			} else {
				const parentNodeFindResult = findParentNode((node) => supportedNodesForAI.has(node.type))(
					selection,
				);
				return parentNodeFindResult?.node;
			}
		};
		const disabled = getIsAiEntryPointDisabled(api);
		const parentSupportedForAIButton = getParentSupportedForAIButton();
		const isAIEnabled = aiGlobalOptIn.status === 'enabled';
		if ((from === to || isNodeSelection || isCellSelection) && parentSupportedForAIButton) {
			const canAddAIButton = isNodeSelection || parentSupportedForAIButton;
			const nodeType = parentSupportedForAIButton?.type;
			if (canAddAIButton && nodeType) {
				const aiIconButton = getAIButton({
					intl,
					editorPluginAIProvider,
					aiGlobalOptIn,
					nodeType,
					disabled,
					api,
					state,
				});

				const separator: FloatingToolbarSeparator = {
					type: 'separator',
					fullHeight: editorExperiment('platform_editor_controls', 'variant1'),
				};

				const addAIButtonToConfig = (title: string) => {
					const toolbarConfig = processedConfigs?.find((config) => config.title === title);
					const items = Array.isArray(toolbarConfig?.items) ? toolbarConfig?.items : [];
					if (toolbarConfig && items.length) {
						const withAIItems =
							(isAIEnabled && fg('ai-smart-button-team-25')) ||
							editorExperiment('platform_editor_controls', 'variant1')
								? [aiIconButton, separator, ...items]
								: [
										...items.slice(0, items.length - 3),
										aiIconButton,
										separator,
										...items.slice(items.length - 3, items.length),
									];
						/**
						 * Updating items of table, panel, layout or expand config item.
						 * Injecting updated items in same config object.
						 */
						toolbarConfig.items = withAIItems;
						return toolbarConfig;
					}
				};

				/**
				 * Here we are updating items of table, panel, expand, layout and card
				 * 	floaingToolbarConfig.
				 * This will only work when editor-plugin-ai is added after these plugins.
				 * Proper solution will be implemented when outcome
				 * 	of https://hello.atlassian.net/wiki/spaces/EDITOR/pages/4166466469/Editor+RFC+058+Easier+way+to+insert+items+in+floating+toolbar
				 *  RFC is finalised.
				 */

				if (
					editorPluginAIProvider.product === 'CONFLUENCE' &&
					expValEquals('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true)
				) {
					const { inlineCard, blockCard, embedCard } = state.schema.nodes;

					const isSmartLinkCardNode = (nodeType: NodeType) =>
						nodeType === inlineCard || nodeType === blockCard || nodeType === embedCard;

					const smartlinkUrl: string | undefined =
						typeof parentSupportedForAIButton?.attrs?.url === 'string'
							? parentSupportedForAIButton.attrs.url
							: undefined;

					if (smartlinkUrl && isAIEnabled && isSmartLinkCardNode(nodeType)) {
						const cardConfig = processedConfigs?.find((config) => {
							const configNodeTypes = Array.isArray(config.nodeType)
								? config.nodeType
								: [config.nodeType];
							return configNodeTypes.some((nt) => isSmartLinkCardNode(nt));
						});

						if (cardConfig) {
							// When the SmartLink is not yet resolved the renderer returns null
							// and no AI buttons or separator appear.
							const smartLinkAIButtons = getSmartLinkAIButtons({
								api,
								aiGlobalOptIn,
								areAnyNewToolbarFlagsEnabled: areToolbarFlagsEnabled(Boolean(api?.toolbar)),
								disabled,
								intl,
								nodeType,
								rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
								smartlinkUrl,
							});

							if (!smartLinkAIButtons) {
								return cardConfig;
							}

							const prependButton = (
								existingItems: Array<FloatingToolbarItem<Command>>,
							): Array<FloatingToolbarItem<Command>> => [smartLinkAIButtons, ...existingItems];

							if (typeof cardConfig.items === 'function') {
								const originalItemsFn = cardConfig.items;
								// when 'Edit link' is active, the card plugin returns a single item (the link
								// picker). in that case we don't need to prepend AI buttons
								cardConfig.items = (node: Node) => {
									const resolvedItems = originalItemsFn(node);
									return resolvedItems.length > 1 ? prependButton(resolvedItems) : resolvedItems;
								};
							} else if (Array.isArray(cardConfig.items) && cardConfig.items.length) {
								cardConfig.items = prependButton(cardConfig.items);
							}
							return cardConfig;
						}
					}
				}

				if (!editorPluginAIProvider.isInDocumentStreamingEnabled) {
					return addAIButtonToConfig('Table floating controls');
				}
			}
		}

		// Plain hyperlink support: detect link marks at the cursor position
		// and inject AI buttons into the hyperlink floating toolbar.
		// Hyperlinks are marks on text nodes, not standalone nodes, so they
		// aren't in getSupportedNodesForAIModal and are handled separately.
		// Allow hyperlink handling when there is no supported parent node, or when the
		// parent is a table (hyperlinks can exist inside table cells). SmartLink card
		// nodes are handled earlier in this function and must not re-enter here.
		const parentIsTable = parentSupportedForAIButton?.type === state.schema.nodes.table;
		if (
			from === to &&
			(!parentSupportedForAIButton || parentIsTable) &&
			isAIEnabled &&
			editorPluginAIProvider.product === 'CONFLUENCE' &&
			expValEquals('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true)
		) {
			const linkAtCursor = getLinkMarkAtCursor(state, from);
			const hyperlinkUrl = linkAtCursor?.url;

			if (hyperlinkUrl && linkAtCursor?.parentNode) {
				const hyperlinkConfig = processedConfigs?.find((config) => {
					const nodeTypes = Array.isArray(config.nodeType) ? config.nodeType : [config.nodeType];
					return nodeTypes.includes(linkAtCursor.parentNode.type);
				});

				if (hyperlinkConfig) {
					const smartLinkAIButtons = getSmartLinkAIButtons({
						api,
						aiGlobalOptIn,
						areAnyNewToolbarFlagsEnabled: areToolbarFlagsEnabled(Boolean(api?.toolbar)),
						disabled,
						intl,
						isHyperlink: true,
						nodeType: linkAtCursor.parentNode.type,
						rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
						smartlinkUrl: hyperlinkUrl,
					});

					if (!smartLinkAIButtons) {
						return hyperlinkConfig;
					}

					const prependButton = (
						existingItems: Array<FloatingToolbarItem<Command>>,
					): Array<FloatingToolbarItem<Command>> => [smartLinkAIButtons, ...existingItems];

					if (Array.isArray(hyperlinkConfig.items) && hyperlinkConfig.items.length > 1) {
						// EDIT mode has multiple toolbar items; INSERT/EDIT_INSERTED mode has
						// a single custom item (the link picker). Only prepend in EDIT mode.
						hyperlinkConfig.items = prependButton(hyperlinkConfig.items);
					}
					return hyperlinkConfig;
				}
			}
		}

		return;
	};
